import { Checkbox } from "@progress/kendo-react-inputs";
import { useState, useEffect, useRef } from "react";
import { getAliceRecommendedAttributes } from "../../../../API/AliceGPT/AliceGPT";
import { ATTRIBUTE_DESELECTED, ATTRIBUTE_PICKLIST_UPDATED, ATTRIBUTE_SELECTED, ATTRIBUTE_TYPE_CHANGED, TOGGLE_SELECT_ALL_ATTRIBUTES } from "../../../../Context/AliceGPT/AliceGPTContextEvents";
import useAliceGPT from "../../../../Hooks/AliceGPT/useAliceGPT";
import EditPicklistDialog from "./EditPicklistDialog";
import Loader from "../../../Shared/Loader/loader";
import InnerLoader from "../../../Shared/Loader/InnerLoader";
import { getDatalakeImageUrl } from "../../../Helpers/DateLakeStorage";
import { convertTypeToDesignerType } from "../../../Helpers/Recommendation/AliceGPT";
import { bindI18nEvents as bindAttributeTypeFieldi18n, typeText } from "../../../Attributes/Fields/AttributeTypeField";
import { Tooltip } from '@progress/kendo-react-tooltip';
import FailedToLoadError from "../../../Shared/Recommendation/Error/FailedToLoadError";
import AttributeTypeDropdown, { bindI18nEvents as bindAttributeTypeDropdowni18n, recommendationAttributeType } from "../../../Shared/Recommendation/AttributeTypeDropdown";
import { ENTITY_LABEL } from "../../../Helpers/Labels/EntityLabel";
import { useTranslation } from "react-i18next";
import Scrollable from "../../../Shared/Edit/Scrollable";

function AliceRecommendedAttributesList({ isFromExtension }) {

    const inputRef = useRef(null);
    const { t } = useTranslation("create_project/alice_recommended_attributes_list", { useSuspense: false });

    const [state, dispatch] = useAliceGPT();

    const [loading, setLoading] = useState({});
    const [fetchingMore, setFetchingMore] = useState({});
    const [picklistAttributeToEdit, setPicklistAttributeToEdit] = useState(null);
    const [editAttributeTypeIndex, setEditAttributeTypeIndex] = useState(-1);

    let numSelectedAttributes = state.selectedEntity.attributes.filter(attribute => attribute.selected).length;

    useEffect(() => {
        bindAttributeTypeFieldi18n()
        bindAttributeTypeDropdowni18n();
    }, []);

    useEffect(() => {
        setEditAttributeTypeIndex(-1);
    }, [state?.selectedEntity?.name]);

    // useEffect(() => {
    //     if (
    //         !loading[state.selectedEntity.name] &&
    //         state.selectedEntity &&
    //         state.selectedEntity.name &&
    //         (
    //             !state.selectedEntity.attributes ||
    //             state.selectedEntity.attributes.length === 0
    //         )
    //     ) {
    //         setLoading(prevValue => ({ ...prevValue, [state.selectedEntity.name]: true }));
    // getAliceRecommendedAttributes(state.selectedEntity.name, state.form.model, "", state.form.domain, state.form.description)
    //     .then(response => {
    //         const attributes = response.map(attributesToStateArrayElement);
    //         dispatch({
    //             type: "ATTRIBUTES_LOADED",
    //             payload: {
    //                 attributes,
    //                 entityName: state.selectedEntity.name
    //             }
    //         });
    //     })
    //     .catch(error => {
    //         console.log(error);
    //     })
    //     .finally(() => {
    //         setLoading(prevValue => ({ ...prevValue, [state.selectedEntity.name]: false }));
    //     });
    //     }
    // }, [state.selectedEntity.name]);

    async function getAttributes() {
        let counter = 0;
        dispatch({ type: "ENTITY_ATTRIBUTE_LOADING_STARTED", payload: { entityName: state.selectedEntity.name } });
        try {
            const attributes = await getAliceRecommendedAttributes(
                state.selectedEntity.name, state.form.model, "", state.form.domain, state.form.description, isFromExtension
            );
            const attributesWithPicklist = attributes.map(attributesToStateArrayElement);
            dispatch({
                type: "ATTRIBUTES_LOADED",
                payload: {
                    attributes: attributesWithPicklist,
                    entityName: state.selectedEntity.name
                }
            });
        } catch (error) {
            dispatch({ type: "ENTITY_ATTRIBUTE_LOADING_FAILED", payload: { entityName: state.selectedEntity.name } });
        } finally {
            counter++;
        }
    }

    function attributesToStateArrayElement(attribute) {
        return {
            name: attribute[0],
            type: convertTypeToDesignerType(attribute[1]),
            picklist: attribute[2] || "",
            required: attribute[3],
            unique: attribute[4],
            checked: false,
            selected: false,
            disabled: attribute.disabled
        }
    }

    function toggleAttributeCheckbox(attribute, index) {
        if (attribute.selected) {
            deselectAttribute(attribute, index);
        } else {
            selectAttribute(attribute, index);
        }
    }

    function selectAttribute(attribute, index) {
        dispatch({
            type: ATTRIBUTE_SELECTED,
            payload: {
                entityName: state.selectedEntity.name,
                attributeName: attribute.name,
                attributeIndex: index
            }
        });
    }

    function deselectAttribute(attribute, index) {
        dispatch({
            type: ATTRIBUTE_DESELECTED,
            payload: {
                entityName: state.selectedEntity.name,
                attributeName: attribute.name,
                attributeIndex: index
            }
        });
    }

    function handleShowMore() {
        setFetchingMore(prevValue => ({ ...prevValue, [state.selectedEntity.name]: true }));
        const attributesToSkip = areSomeAttributesSelected()
            ? getSelectedAttributesString()
            : getAllAttributesString();
        getAliceRecommendedAttributes(state.selectedEntity.name, state.form.model, attributesToSkip, state.form.domain, state.form.description, state.form.language?.code, isFromExtension)
            .then(response => {
                const attributes = response.map(attributesToStateArrayElement);
                dispatch({
                    type: "ATTRIBUTES_ADDED",
                    payload: {
                        attributes,
                        entityName: state.selectedEntity.name
                    }
                });
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                setFetchingMore(prevValue => ({ ...prevValue, [state.selectedEntity.name]: false }));
            });
    }

    function areSomeAttributesSelected() {
        return state.selectedEntity.attributes.some(attribute => attribute.selected);
    }

    function getSelectedAttributesString() {
        return state.selectedEntity.attributes
            .filter(attribute => attribute.selected)
            .map(attribute => attribute.name)
            .join(",");
    }

    function getAllAttributesString() {
        return state.selectedEntity.attributes
            .map(attribute => attribute.name)
            .join(",");
    }

    function handleEditPicklistClick(attribute) {
        setPicklistAttributeToEdit(attribute);
    }

    function handlePicklistUpdate(picklist) {
        dispatch({
            type: ATTRIBUTE_PICKLIST_UPDATED,
            payload: {
                entityName: state.selectedEntity.name,
                attributeName: picklistAttributeToEdit.name,
                picklist
            }
        });
        handleEditPicklistClick(null);
    }

    function handleAttributeTypeClick(event, index) {
        setEditAttributeTypeIndex(index);
    }

    function handleAttributeTypeChange(event, index) {
        dispatch({
            type: ATTRIBUTE_TYPE_CHANGED,
            payload: {
                entityName: state.selectedEntity.name,
                index,
                type: event.target.value.value
            }
        })
        clearEditAttributeIndex()
    }

    function clearEditAttributeIndex() {
        setEditAttributeTypeIndex(-1);
    }

    function getAttributeType(attributeType) {
        const type = recommendationAttributeType.find((item) => item.value === attributeType);
        return type ? type.text : "";
    }

    function toggleEditAttribute(index) {
        return () => {
            dispatch({
                type: "TOGGLE_EDIT_ATTRIBUTE",
                payload: {
                    entityName: state.selectedEntity.name,
                    attributeIndex: index
                }
            })
        }
    }

    function handleAttributeNameChange(index) {
        return (event) => {
            dispatch({
                type: "ATTRIBUTE_NAME_CHANGED",
                payload: {
                    entityName: state.selectedEntity.name,
                    attributeIndex: index,
                    newAttributeName: inputRef.current.value
                }
            })
            dispatch({
                type: "TOGGLE_EDIT_ATTRIBUTE",
                payload: {
                    entityName: state.selectedEntity.name,
                    attributeIndex: index
                }
            })
        }
    }

    function hasDuplicateAttributeNames() {
        return state.selectedEntity.attributes.some((attribute, index) => attribute.selected && isNameReused(attribute.name, index));
    }

    function hasEmptyAttributeNames() {
        return state.selectedEntity.attributes.some(attribute => attribute.selected && attribute.name.trim().length === 0);
    }

    function hasAttributeNameStartingWithNumber() {
        return state.selectedEntity.attributes.some(attribute => attribute.selected && /^\d/g.test(attribute.name));
    }

    function hasMultipleStateFields() {
        return state.selectedEntity.attributes.filter(attribute => attribute.selected && attribute.type === "State Field").length > 1;
    }

    function hasAttributeNameWithMoreThan30Characters() {
        return state.selectedEntity.attributes.some(attribute => attribute.selected && attribute.name.length > 30);
    }

    function isNameReused(name, index) {
        const isSelected = state.selectedEntity.attributes[index].selected;
        if (isSelected) {
            return state.selectedEntity
                        .attributes
                        .some((attribute, aI) => (
                            attribute.selected &&
                            attribute.name.trim().toLowerCase() === name.trim().toLowerCase() &&
                            aI !== index
                        ));
        }
        return false;
    }

    function isMultipleStateAttirbutesType(type, index) {
        if (type === "State Field") {
            const isCurrentSelected = state.selectedEntity.attributes[index].selected;
            if (isCurrentSelected) {
                return state.selectedEntity.attributes.some((attribute, aI) => (
                    attribute.selected && attribute.type === type && aI !== index
                ));
            }
            return false; 
        }
    }

    if (state.selectedEntity && state.selectedEntity.name && state.selectedEntity.attributesLoading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 w-100">
                <img src={getDatalakeImageUrl("/loaders/RecommendationLoader.gif")} alt="" />
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        )
    }

    if (state.selectedEntity && state.selectedEntity.name && state.selectedEntity.hasAttributeLoadingError) {
        return (
            <FailedToLoadError />
        )
    }

    return (
        <Scrollable>
            {
                picklistAttributeToEdit &&
                <EditPicklistDialog
                    title={picklistAttributeToEdit.type === "State Field" ? t("edit_dropdown_dialog.edit_state_title") : t("edit_dropdown_dialog.edit_dropdown_title")}
                    addOptionText={picklistAttributeToEdit.type === "State Field" ? t("edit_dropdown_dialog.add_state_text") : t("edit_dropdown_dialog.add_dropdown_text")}
                    attribute={picklistAttributeToEdit}
                    onCancel={() => handleEditPicklistClick(null)}
                    onClose={() => handleEditPicklistClick(null)}
                    onSubmit={handlePicklistUpdate} />
            }
            <ul className="list-group">
                <li className="list-group-item">
                    <h5 className="d-flex align-items-center">
                        <Checkbox
                            checked={state.selectedEntity.attributes.every(attribute => attribute.selected)}
                            value={state.selectedEntity.attributes.every(attribute => attribute.selected)}
                            onChange={() => {
                                dispatch({
                                    type: TOGGLE_SELECT_ALL_ATTRIBUTES,
                                    payload: { entityName: state.selectedEntity.name }
                                })
                            }} />
                        &nbsp;
                        <Tooltip position='right' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }} className="mt-2">
                            <span className="mb-0 ms-1" style={{ fontWeight: "700" }}>
                                {t("drawer.select_all.label")} &nbsp;
                                <i
                                    className='fa-solid fa-circle-info font-size-3' style={{ opacity: "0.5" }}
                                    title={t("drawer.select_all.tooltip")}
                                />
                            </span>
                        </Tooltip>
                    </h5>
                    {
                        numSelectedAttributes > state.subscription.maxAttributesPerApp &&
                        <div className="text-danger">
                            {t("drawer.max_attributes_exceeded", { maxAttributes: state.subscription.maxAttributesPerApp })}
                        </div>
                    }
                    {
                        hasDuplicateAttributeNames() &&
                        <div className="text-danger">
                            {t("drawer.duplicate_attribute_names")}
                        </div>
                    }
                    {
                        hasEmptyAttributeNames() &&
                        <div className="text-danger">
                            {t("drawer.blank_attribute_names")}
                        </div>
                    }
                    {
                        hasAttributeNameStartingWithNumber() &&
                        <div className="text-danger">
                            {t("drawer.attribute_names_starting_with_number")}
                        </div>
                    }
                    {
                        hasMultipleStateFields() &&
                        <div className="text-danger">
                            {t("drawer.multiple_state_attributes")}
                        </div>
                    }
                    {
                        hasAttributeNameWithMoreThan30Characters() &&
                        <div className="text-danger">
                            {t("drawer.attribute_names_more_than_30_characters")}
                        </div>
                    }
                </li>
                {
                    state.selectedEntity.attributes &&
                    state.selectedEntity.attributes.map((attribute, index) => (
                        <li key={index} className="list-group-item recommended-item-with-edit">
                            {
                                attribute.inEdit ?
                                    <div className="w-100">
                                        <input
                                            ref={inputRef}
                                            className="form-control w-25 d-inline-block"
                                            defaultValue={attribute.name}
                                            onKeyDown={e => {
                                                if (e.key === "Enter") {
                                                    handleAttributeNameChange(index)(e);
                                                }
                                            }}
                                        />
                                        &nbsp;&nbsp;&nbsp;
                                        <button className="btn p-0 border-0" onClick={handleAttributeNameChange(index)}>
                                            <i className="fa-solid fa-check text-success" />
                                        </button>
                                    </div>
                                    :
                                    <>
                                        <Checkbox
                                            disabled={attribute.disabled}
                                            checked={attribute.selected}
                                            onChange={() => toggleAttributeCheckbox(attribute, index)}
                                            label={attribute.name}
                                        />
                                        &nbsp;
                                        {
                                            (
                                                isNameReused(attribute.name, index) ||
                                                attribute.name.trim().length === 0 ||
                                                /^\d/g.test(attribute.name) ||
                                                isMultipleStateAttirbutesType(attribute.type, index) ||
                                                attribute.name.trim().length > 30
                                            ) &&
                                            <span className="text-danger">
                                                <i className='fa-solid fa-circle-info text-danger' />
                                            </span>
                                        }
                                        -
                                        &nbsp;
                                        {
                                            editAttributeTypeIndex === index ?
                                                <div className="d-inline-block w-25">
                                                    <div className="d-inline-block w-90">
                                                        <AttributeTypeDropdown
                                                            value={attribute.type}
                                                            onChange={(event) => handleAttributeTypeChange(event, index)} />
                                                    </div>
                                                    &nbsp;&nbsp;
                                                    <button className="btn btn-link m-0 p-0 text-danger" onClick={clearEditAttributeIndex}>
                                                        <i className="fa-solid fa-close" />
                                                    </button>
                                                </div>
                                                :
                                                <button
                                                    className={
                                                        attribute.isAzureAttribute ?
                                                            "btn text-dark border-0 m-0 p-0 cursor-default" :
                                                            "btn btn-link border-0 m-0 p-0"
                                                    }
                                                    onClick={(e) => {
                                                        if (!attribute.isAzureAttribute) {
                                                            handleAttributeTypeClick(e, index)
                                                        }
                                                    }}
                                                >
                                                    {getAttributeType(attribute.type)}
                                                </button>
                                        }
                                        {
                                            (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                                            attribute.picklist.length > 0 &&
                                            ` - ${attribute.picklist.join(", ")}`
                                        }
                                        {
                                            (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                                            <button
                                                className="btn btn-link py-0"
                                                onClick={() => handleEditPicklistClick(attribute)}>
                                                {t("drawer.edit")} {attribute.type === "Picklist String" ? t("drawer.dropdown") : t("drawer.state")} {t("drawer.values")}
                                            </button>
                                        }
                                        {
                                            !attribute.isAzureAttribute &&
                                            <button className="btn p-0 border-0 ms-2 recommendation-edit-name-button text-link" onClick={toggleEditAttribute(index)}>
                                                <i className="fal fa-edit font-size-0" />
                                            </button>
                                        }
                                    </>
                            }
                            {
                                (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                                attribute.picklist.length === 0 &&
                                <span className="ms-2 text-danger">
                                    {
                                        attribute.type === "Picklist String" ?
                                            t("drawer.no_dropdown_values") :
                                            t("drawer.no_state_values")
                                    }
                                </span>
                            }
                        </li>
                    ))}
                {
                    !loading[state.selectedEntity.name] &&
                    !state.selectedEntity.hadNoMoreAttributes &&
                    <li className="list-group-item">
                        <button
                            className="btn btn-link px-0 font-size-2"
                            onClick={handleShowMore}
                            disabled={fetchingMore[state.selectedEntity.name]}
                        >
                            {t("drawer.show_more")} {fetchingMore[state.selectedEntity.name] && <InnerLoader borderColor="green" />}
                        </button>
                    </li>
                }
                {
                    state.selectedEntity.hadNoMoreAttributes &&
                    <span className="ms-3">
                        {t("drawer.no_more_recommendations")}
                    </span>
                }
            </ul>
        </Scrollable>
    );
}

export default AliceRecommendedAttributesList;